// Utilities
import {
  accordions,
  tabsInit,
  toTimestamp,
  getTemplate,
  mountAppStoreButton,
  isMobile,
  smoothScroll,
  showErrorMessage,
  productTranslations
} from '../utilities'

// Tabs/Accordions Sections
import {
  bookDimensions,
  publicationDate,
  getReviews,
  getContributorBios,
  getVideos,
  getTOC,
  getResources,
  getAudioSample,
  getShortDescription,
  bookDataOverrides,
  getBisacs,
  getAges,
  getBookInteriorImages,
  getAccessibilityInfo
} from '../utils/get-book-info'

// Get SVGS
import { getInfoSVG, getQuoteSVG, getResizeSVG } from '../utils/get-svgs'

// Product page data functions
import { getFormatsDropdown, getFormatButtons, getCurrentFormatName } from '../utils/get-formats'
import { otherCoversModal } from '../utils/other-covers-modal'
import getRetailers from '../utils/get-retailers'
import { getPriceSection, priceString } from '../utils/get-price'
import { getBookPreviewLink } from '../utils/get-preview-link'
import { contributorsList } from '../utils/get-contributors-list'
import { getExternalPromoBoxes } from '../utils/get-promo-boxes'

/**
 * This class is responsible for running the Product Details page
 */
export class ProductDetails {
  constructor (client, isbn) {
    this.client = client
    this.isbn = isbn
    this.loader = document.getElementById('product-loader')
    this.productContainer = document.querySelector('.product-section')
    this.pageEvents = []
    this.isSupaduProductPage = !!document.querySelector('.js-supadu-product-page')
  }

  /**
   * Accepts a function reference to be to be run at the top of the addEventListeners method
   * @param {Function} newEvent
   */
  addPageEvent (newEvent) {
    this.pageEvents.push(newEvent)
  }

  runPageEvent (currentEvent) {
    currentEvent()
  }

  initCoverImage (bookData) {
    if (!bookData || !bookData.image || !bookData.title) {
      return
    }
    const imageWrapper = document.querySelector('#products-details-image')
    if (!imageWrapper) {
      return
    }

    let imgSrc = bookData.image
    const imgTitle = bookData.title
    const imageWidth = imageWrapper.dataset.imageWidth || '300'

    if (imageWrapper.dataset.imageWidth) {
      imgSrc = new URL(imgSrc)
      // Imgix images with blend param break when setting width param
      if (!imgSrc.searchParams.has('blend')) {
        imgSrc.searchParams.set('w', imageWidth)
      }
      if (imgSrc.href.includes('x145')) {
        imgSrc.href = imgSrc.href.replace('x145', `x${imageWrapper.dataset.imageWidth}`)
      }
      if (imgSrc.href.includes('cover_290H')) {
        imgSrc.href = imgSrc.href.replace('cover_290H', 'cover_648H')
      }
      if (imgSrc.href.includes('_109W')) {
        imgSrc.href = imgSrc.href.replace('_109W', '_1000H')
      }
      imgSrc.search = imgSrc.searchParams.toString()
      imgSrc = imgSrc.toString()
    }

    const imgHTML = `
      <img
        src="${imgSrc}"
        alt="${imgTitle}"
        width="${imageWidth}"
        height="450"
        loading="eager"
        onload="this.style.opacity='1'"
      >
    `
    imageWrapper.innerHTML = imgHTML

    if (window.theme && window.theme.settings && window.theme.settings.bookDetailsSettings && window.theme.settings.bookDetailsSettings.loaderType === 'skeleton') {
      this.hideImageSkeleton()
    }

    imageWrapper.style.display = 'block'
  }

  async initProductFormOtherLinks (bookData) {
    const otherLinksWrapper = document.querySelector('.js-product-form__other-links')

    if (!bookData || !bookData.image || !otherLinksWrapper) {
      return
    }

    // Google Preview Link HTML
    const getPreviewLink = await getBookPreviewLink(bookData)
    if (getPreviewLink) {
      otherLinksWrapper.innerHTML += getPreviewLink
    }

    // Enlarge Book Cover HTML
    const imgSrc = bookData.image.replace('w=298', 'w=1000').replace('x145', 'x1000')
    const resizeSvg = getResizeSVG()
    if (imgSrc) {
      otherLinksWrapper.innerHTML += `<li><a href="${imgSrc}" title="${bookData.languages.enlarge_book_cover}" target="_blank"><span>${bookData.languages.enlarge_book_cover}</span>${resizeSvg}</a></li>`
    }
    // Audio Sample HTML
    const audioSampleWrapper = document.querySelector('.js-audio-sample')
    const audioSample = getAudioSample(bookData)
    if (audioSampleWrapper && audioSample) {
      audioSampleWrapper.innerHTML += audioSample
    }
  }

  addEventListeners () {
    if (this.pageEvents && this.pageEvents.length) {
      this.pageEvents.forEach(newEvent => {
        this.runPageEvent(newEvent.bind(this))
      })
    }

    this.initCoverImage(this.bookData)

    // Read More contributorsList
    const readMore = document.querySelectorAll('.product-contributors .js-read-more')
    if (readMore && readMore.length) {
      readMore.forEach((button) => {
        button.addEventListener('click', e => {
          const element = e.currentTarget
          element.classList.add('hide')
          const { parentElement } = element
          parentElement.querySelector('.js-other-contributors').classList.remove('hide')
        })
      })
    }

    // Fill Formats Dropdown Array
    if (document.querySelector('.js-formats-dropdown')) {
      document.querySelector('.js-formats-dropdown').innerHTML = this.bookData.templateVars.formatsDropdown
    }

    // Fill Retailers Section
    if (document.querySelector('.js-product-form__retailers') && this.bookData.templateVars.retailers) {
      document.querySelector('.js-product-form__retailers').innerHTML = this.bookData.templateVars.retailers
    }

    // Fill Mobile Description
    if (document.querySelector('.js-product__description.mobile')) {
      document.querySelector('.js-product__description.mobile').innerHTML = this.bookData.templateVars.shortDescription
    }

    // Smooth Scroll Description
    if (document.querySelector('.js-product__description-more')) {
      document.querySelectorAll('.js-product__description-more').forEach((readMore) => {
        readMore.addEventListener('click', (e) => {
          e.preventDefault()
          if (!isMobile()) {
            document.querySelector('.js-description-tab').click()
            smoothScroll(document.querySelector('.js-product-tabs'))
          } else {
            const accordionDescription = document.querySelector('.js-accordionDescription')
            if (!accordionDescription.parentElement.classList.contains('open')) {
              accordionDescription.click()
            }
            smoothScroll(document.querySelector('.js-desc-acc'))
          }
        })
      })
    }

    // Init ProductFormOtherLinks
    this.initProductFormOtherLinks(this.bookData)

    // Activate click on formats dropdown
    const dropdownsLinksToggle = document.querySelectorAll(
      '.dropdown__inner span, .dropdown__inner li:first-child'
    )

    if (dropdownsLinksToggle) {
      dropdownsLinksToggle.forEach(function (element) {
        element.addEventListener('click', (e) => {
          e.currentTarget.classList.toggle('open')
        })
        element.addEventListener('keyup', e => {
          if (e.keyCode === 9) {
            e.currentTarget.classList.add('open')
          }
        })
      })
    }

    // Show Hide Sections
    if (document.querySelector('.product-section')) {
      document.querySelector('.product-section').style.display = 'block'
    }

    if (document.getElementById('product-loader')) {
      document.getElementById('product-loader').style.display = 'none'
    }

    if (document.querySelector('.js-supafolio-loader')) {
      document.querySelector('.js-supafolio-loader').style.display = 'none'
    }

    const imageContainer = document.getElementById('products-details-image')
    if (imageContainer) {
      imageContainer.style.display = 'block'
    }

    // product images modal
    if (document.querySelectorAll('#product-details-other-covers img').length) {
      const otherCovers = document.querySelectorAll('#product-details-other-covers img')
      if (otherCovers.length > 3) {
        // eslint-disable-next-line no-undef
        $('#product-details-other-covers').slick({
          infinite: true,
          slidesToShow: 3,
          slidesToScroll: 3
        })
        // eslint-disable-next-line no-undef
        $('#product-details-other-covers').slick('refresh')
      }
      otherCoversModal(document.querySelectorAll('#product-details-other-covers img'))
    }

    // Move Judge.me widget
    const badge = document.querySelector('.jdgm-preview-badge')
    const contributors = document.querySelector('.product-vendor')
    if (badge && contributors) {
      contributors.insertAdjacentElement('afterend', badge)
    }

    accordions()
  }

  /**
   * Returns altered book item object
   * @param {Object} item
   * @returns {Object}
   */
  resultsHandler (item) {
    const templateVars = {}
    const otherRetailers = ''
    let showBuyButton = true
    // Languages
    item.languages = productTranslations()

    /**
     * Apple App Store Link
     */
    const retailersData = Array.isArray(item.retailers) ? item.retailers : []
    mountAppStoreButton(retailersData)

    /* Loop through supplementals */
    if (item.assets && item.assets.length) {
      item.assets.forEach((asset) => {
        if (!asset.asset) {
          return
        }

        if (
          asset.asset.type === 'pdf' &&
          asset.asset.sub_type === 'excerpt' &&
          asset.asset.path &&
          document.getElementById('excerpt-link')
        ) {
          document.getElementById('excerpt-link').innerHTML =
            '<a class="btn btn--full" href="' +
            asset.asset.path.replace(/<(?:.|\n)*?>/gm, '') +
            '" target="_blank">Read an Excerpt</a>'
        }
      })
    }

    /* Check Other Formats Handle */
    let otherFormatHandles = null
    // get the custom value format handles & child_handles
    if (item.custom && item.custom.formats_array) {
      otherFormatHandles = item.custom.formats_array
    }

    // Get Format Variants
    const formatVariants = []
    if (otherFormatHandles && item.formats && item.formats.length) {
      /* Loop through other Formats */
      item.formats.forEach((otherFormat) => {
        if (!otherFormat.format) {
          return
        }
        if (
          otherFormatHandles[otherFormat.isbn] &&
          otherFormat.isbn &&
          otherFormat.format.name &&
          otherFormat.format.code
        ) {
          formatVariants.push({
            isbn: otherFormat.isbn || '',
            format: otherFormat.format && otherFormat.format.name ? otherFormat.format.name : '',
            code: otherFormat.format && otherFormat.format.code ? otherFormat.format.code : '',
            handle: otherFormatHandles[otherFormat.isbn]
          })
        }
      })
    }

    templateVars.formatsDropdown = getFormatsDropdown(item)
    templateVars.formatButtons = getFormatButtons(item)

    templateVars.formatVariants = formatVariants
    templateVars.contributorList = contributorsList(item.contributors)

    /* Retailer links (Old Product Page) */
    if (otherRetailers) {
      document.getElementById('retailer-buttons').innerHTML =
        '<div class="dropdown__inner"><span>Other Retailers</span><ul>' +
        otherRetailers +
        '</ul></div>'
    }

    /*
    Check if book is published before date defined in admin and out of stock
    */
    let today = new Date()
    let showBuyButtonUntilDays = 7
    const bookDetailsSettings = window.theme.settings.bookDetailsSettings
    if (bookDetailsSettings && bookDetailsSettings.days_before_buy_now) {
      showBuyButtonUntilDays = bookDetailsSettings.days_before_buy_now
    }
    let showBuyButtonUntilDaysDate = new Date(today.getTime() + showBuyButtonUntilDays * 24 * 60 * 60 * 1000)
    let preOrderLabel = 'Pre-Order'
    if (item.languages && item.languages.preorder) {
      preOrderLabel = item.languages.preorder
    }
    const addToCart = document.querySelector('.add-to-cart')
    let publicationDateTimestamp = ''
    if (item.sale_date && item.sale_date.date) {
      publicationDateTimestamp = new Date(item.sale_date.date.replace(/ /g, 'T'))
    }
    let addToCartText = null
    if (addToCart) {
      addToCartText = addToCart.firstChild.nextSibling
    }

    // convert all dates to timestamp
    today = toTimestamp(today)
    showBuyButtonUntilDaysDate = toTimestamp(showBuyButtonUntilDaysDate)
    publicationDateTimestamp = toTimestamp(publicationDateTimestamp)
    if (
      publicationDateTimestamp > today &&
      publicationDateTimestamp <= showBuyButtonUntilDaysDate &&
      addToCartText
    ) {
      addToCartText.innerHTML = preOrderLabel
    }

    // check hidebuybutton attribute
    if (
      item.custom.hidebuybutton &&
      item.custom.hidebuybutton === true
    ) {
      showBuyButton = false
    }

    // check outofstock attribute
    if (
      item.custom.outofstock &&
      item.custom.outofstock === true &&
      addToCartText
    ) {
      addToCart.setAttribute('disabled', true)
      addToCartText.innerHTML = 'Out of Stock'
    }

    // check if no price listed in data
    if (!window.theme.productData.price && addToCart) {
      addToCart.setAttribute('disabled', true)
      addToCartText.innerHTML = 'Product Unavailable'
      const quantityButtons = document.querySelector('.product__quantity')
      if (quantityButtons) {
        quantityButtons.style.display = 'none'
      }
    }

    const productForm = document.querySelector('.js-product-single__form')
    if (productForm && (!showBuyButton || publicationDateTimestamp >= showBuyButtonUntilDaysDate)) {
      if (bookDetailsSettings.not_yet_published_message) {
        productForm.innerHTML = `
          <p class='out_of_stock_message'>
            ${bookDetailsSettings.not_yet_published_message}
          </p>
        `
      } else {
        productForm.style.display = 'none'
      }
    }

    // Price Section
    templateVars.priceSection = getPriceSection()

    // Book Interior Images
    getBookInteriorImages(item)

    // Promo Boxes
    getExternalPromoBoxes(item)

    // Show/Hide Book Details values
    const showBookDetails = window.theme.settings.showBookDetails

    templateVars.shortDescription = getShortDescription(item, showBookDetails)
    // Details
    templateVars.showBookDetails = showBookDetails
    // Book Dimensions
    templateVars.dimensions = bookDimensions(item)
    // Set Publication Date
    templateVars.formattedPubDate = publicationDate(item)
    // Reviews
    item.reviews = getReviews(item)
    // Author Bio
    templateVars.contributorBios = getContributorBios(item)

    // Resources
    templateVars.resources = getResources(item)

    // Audio Sample
    templateVars.audioSample = getAudioSample(item)

    // Retailers
    templateVars.retailers = getRetailers(item)

    // Accessibility
    templateVars.accessibilityInfo = getAccessibilityInfo(item)

    // State
    templateVars.state = item.custom && item.custom.State && item.custom.State.name ? item.custom.State.name : ''

    item.templateVars = templateVars

    // Override data
    bookDataOverrides(item)

    // Hide skeletons only if using skeleton loader
    if (window.theme && window.theme.settings && window.theme.settings.bookDetailsSettings && window.theme.settings.bookDetailsSettings.loaderType === 'skeleton') {
      this.hideContentSkeletons()
      this.hideLinksSkeleton()
    }

    return item
  }

  resultsTabsHandler (item) {
    const templateVars = {}

    // Languages
    item.languages = productTranslations()

    // Details Tab
    templateVars.showBookDetails = window.theme.settings.showBookDetails

    // Price Section
    templateVars.price = priceString(window.theme.productData)

    // Book Dimensions
    templateVars.dimensions = bookDimensions(item)

    // Set Publication Date
    templateVars.formattedPubDate = publicationDate(item)

    // Get Bisacs
    templateVars.bisacs = getBisacs(item)

    // Get Age
    templateVars.age = getAges(item)

    // Current Format
    templateVars.currentFormat = getCurrentFormatName(item)

    // Tabs Label
    if (window.theme.settings.tabsLabel) {
      templateVars.tabsLabel = window.theme.settings.tabsLabel
    }

    // Author Bio Tab
    templateVars.contributorBios = getContributorBios(item)

    // Reviews Tab
    item.reviews = getReviews(item)

    const assets = item.assets

    // Video Tabs
    templateVars.videos = getVideos(assets)

    // Content Tabs
    templateVars.contents = getTOC(assets)

    // Resources
    templateVars.resources = getResources(item)

    // svgIcons
    templateVars.svgIcons = {
      reviews: getQuoteSVG(),
      details: getInfoSVG()
    }

    /*
    set TemplateVars
    */
    item.templateVars = templateVars

    // Override data
    bookDataOverrides(item)

    // Hide tabs skeleton only if using skeleton loader
    if (window.theme.settings.bookDetailsSettings.loaderType === 'skeleton') {
      this.hideTabsSkeleton()
    }

    return item
  }

  /**
   * Return options for use by addDetailsWidget method, and add bookData to class instance in the resultsHandler
   *
   * @param {String} isbn13
   */
  productPageOptions (isbn13) {
    const self = this
    const resultsHandler = self.resultsHandler.bind(self)

    return {
      container: '#products-details-wrapper',
      objectID: isbn13,
      source: 'products',
      getContributorSupplementals: true,
      static: {
        products: true
      },
      templates: {
        item: getTemplate('product-details')
      },
      resultHandler (item) {
        window.theme.staticProductData = item
        self.bookData = item
        return resultsHandler(item)
      }
    }
  }

  // Product tab options
  productTabOptions (isbn13) {
    const self = this
    const resultsTabsHandler = self.resultsTabsHandler.bind(self)

    return {
      container: '#product-tabs-wrapper',
      objectID: isbn13,
      source: 'products',
      getContributorSupplementals: false,
      static: {
        products: true
      },
      templates: {
        item: getTemplate('product-tabs')
      },
      resultHandler (item) {
        self.bookData = item
        return resultsTabsHandler(item)
      }
    }
  }

  /**
   * @returns {*}
   */
  async run () {
    try {
      if (this.isbn && this.isbn.length) {
        await this.client
          .addDetailsWidget(this.productPageOptions(this.isbn))
          .then(() => {
            this.addEventListeners()
          })
      } else {
        showErrorMessage(this.productContainer, this.loader)
      }
    } catch (err) {
      console.log(err)
    }

    // Load product tabs on supadu product page
    if (document.querySelector('.js-supadu-product-page') !== null) {
      try {
        if (this.isbn.length > 0) {
          this.client
            .addDetailsWidget(this.productTabOptions(this.isbn))
            .then(() => {
              tabsInit()
            })
        } else {
          showErrorMessage(this.productContainer, this.loader)
        }
      } catch (err) {
        console.log(err)
      }
    }
  }

  hideImageSkeleton () {
    const imageSkeleton = document.querySelector('#image-skeleton').closest('.skeleton-content')
    const imageContainer = document.getElementById('products-details-image')

    if (imageSkeleton) {
      imageSkeleton.style.display = 'none'
    }
    if (imageContainer) {
      imageContainer.style.display = 'block'
    }
  }

  hideContentSkeletons () {
    const contentSkeleton = document.querySelector('#content-skeleton')

    if (contentSkeleton) {
      contentSkeleton.style.display = 'none'
    }
  }

  hideLinksSkeleton () {
    const linksSkeleton = document.querySelector('#links-skeleton')
    if (linksSkeleton) {
      linksSkeleton.style.display = 'none'
    }
  }

  hideTabsSkeleton () {
    const tabsSkeleton = document.querySelector('.supadu-product-tabs .skeleton-content')
    if (tabsSkeleton) {
      tabsSkeleton.style.display = 'none'
    }
  }
}
